/**
 * Plan complexe, mode « Point » : on déplace M d'affixe z ; forme algébrique,
 * module, argument, formes trigonométrique et exponentielle ; au choix le
 * conjugué, l'opposé et iz (le quart de tour).
 */

import { useRef, useState } from 'react';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import { ToggleChip } from '../../ui/controls';
import { frenchSpaces } from '../../ui/format';
import { snapPoint } from '../../ui/frame';
import { DragHandle, HaloText, INK, VectorArrow } from '../../ui/svg-parts';
import { useDrag } from '../../ui/use-drag';
import {
  argument,
  cosSin,
  modAtoms,
  modPlain,
  modulus,
  modulusTex,
  numTex,
  polarForms,
  zPlain,
  zTex,
  type Z,
} from './complex';
import {
  Box,
  Formula,
  Live,
  Plane,
  PointMark,
  RES_COLOR,
  SOFT,
  VP,
  Z1_COLOR,
  Z2_COLOR,
  arcMid,
  arcPath,
  frame,
  keepInside,
  px,
} from './parts';

export function PointMode({ onSend, sendCost }: ToolProps) {
  const [z, setZ] = useState<Z>({ a: 2, b: 2 });
  const [conj, setConj] = useState(false);
  const [opp, setOpp] = useState(false);
  const [rot, setRot] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);
  const drag = useDrag<'z'>({
    svgRef,
    frame,
    ids: ['z'],
    pointOf: () => ({ x: z.a, y: z.b }),
    onDrag: (_, p) => {
      const q = snapPoint(p, VP, 0.5, 0.5);
      setZ({ a: q.x, b: q.y });
    },
    keyStep: 0.5,
  });

  const m = modulus(z);
  const g = argument(z);
  const cs = cosSin(z);
  const forms = polarForms(z);
  const zero = m.value === 0;
  const O = px({ a: 0, b: 0 });
  const M = px(z);
  const theta = g.value;
  const rMid = arcMid(0, theta);
  const zc: Z = { a: z.a, b: -z.b };
  const zo: Z = { a: -z.a, b: -z.b };
  const zi: Z = { a: -z.b, b: z.a };
  const R = m.value * frame.unit;
  const mLab = keepInside(M.x, M.y, {
    dx: zero ? 14 : (z.a >= 0 ? 1 : -1) * 15,
    dy: z.b >= 0 ? -15 : 15,
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <ToggleChip on={conj} onClick={() => setConj((v) => !v)}>
          Conjugué
        </ToggleChip>
        <ToggleChip on={opp} onClick={() => setOpp((v) => !v)}>
          Opposé
        </ToggleChip>
        <ToggleChip on={rot} onClick={() => setRot((v) => !v)}>
          Fois i
        </ToggleChip>
      </div>

      <Plane
        svgRef={svgRef}
        label={`Plan complexe : le point M d’affixe z = ${zPlain(z)}`}
        svgProps={drag.svgProps}
      >
        {!zero && (
          <>
            <circle
              cx={O.x}
              cy={O.y}
              r={R}
              fill="none"
              stroke={SOFT}
              strokeWidth={1.25}
              strokeDasharray="4 5"
            />
            {/* projections sur les axes : a et b */}
            <line x1={M.x} y1={M.y} x2={M.x} y2={O.y} stroke={SOFT} strokeDasharray="3 4" />
            <line x1={M.x} y1={M.y} x2={O.x} y2={M.y} stroke={SOFT} strokeDasharray="3 4" />
            <path
              d={arcPath(O.x, O.y, 24, 0, theta)}
              fill="none"
              stroke={Z1_COLOR}
              strokeWidth={2}
            />
            {Math.abs(theta) > 0.2 && (
              <HaloText
                x={O.x + 38 * Math.cos(rMid)}
                y={O.y - 38 * Math.sin(rMid)}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={14}
                fontStyle="italic"
                fill={INK}
                aria-hidden
              >
                θ
              </HaloText>
            )}
          </>
        )}
        {conj && !zero && (
          <>
            <line
              x1={M.x}
              y1={M.y}
              x2={px(zc).x}
              y2={px(zc).y}
              stroke={Z2_COLOR}
              strokeDasharray="5 4"
            />
            <PointMark z={zc} color={Z2_COLOR} label="z" overline testId="cp-conj" />
          </>
        )}
        {opp && !zero && (
          <>
            <line
              x1={M.x}
              y1={M.y}
              x2={px(zo).x}
              y2={px(zo).y}
              stroke={Z2_COLOR}
              strokeDasharray="5 4"
            />
            <PointMark z={zo} color={Z2_COLOR} label="−z" testId="cp-opp" />
          </>
        )}
        {rot && !zero && (
          <>
            <path
              d={arcPath(O.x, O.y, R, theta, theta + Math.PI / 2)}
              fill="none"
              stroke={RES_COLOR}
              strokeWidth={2}
              strokeDasharray="5 4"
            />
            <VectorArrow
              x1={O.x}
              y1={O.y}
              x2={px(zi).x}
              y2={px(zi).y}
              color={RES_COLOR}
              inset={7}
            />
            <PointMark z={zi} color={RES_COLOR} label="iz" testId="cp-iz" />
          </>
        )}
        <VectorArrow x1={O.x} y1={O.y} x2={M.x} y2={M.y} color={Z1_COLOR} inset={8} />
        <HaloText x={O.x - 13} y={O.y + 16} fontSize={14} fontWeight={700} fill={INK} aria-hidden>
          O
        </HaloText>
        <DragHandle
          x={M.x}
          y={M.y}
          color={Z1_COLOR}
          label="M"
          labelDx={mLab.dx}
          labelDy={mLab.dy}
          name={`Point M d’affixe z = ${zPlain(z)}`}
          active={drag.active === 'z'}
          keyProps={drag.keyProps('z')}
        />
      </Plane>

      <Live
        text={`z = ${zPlain(z)}, module ${modPlain(m)}${zero ? '' : `, argument ${g.exact ? '' : 'environ '}${g.plain.replace('≈ ', '')}`}`}
      />
      <div className="space-y-2">
        <Box title="Forme algébrique" testId="cp-algebraic">
          <Formula tex={`z = ${zTex(z)}`} />
          <Formula
            tex={`\\operatorname{Re}(z) = ${numTex(z.a)} \\qquad \\operatorname{Im}(z) = ${numTex(z.b)}`}
          />
        </Box>
        <Box
          title="Module : la distance OM"
          testId="cp-modulus"
          action={
            onSend && (
              <SendToSheetButton
                label={modPlain(m)}
                cost={sendCost}
                onClick={() => onSend({ atoms: modAtoms(m) })}
              />
            )
          }
        >
          <Formula tex={modulusTex(z)} />
        </Box>
        <Box title="Argument : l’angle θ" testId="cp-argument">
          {zero || !cs ? (
            <p className="text-ink-700 text-sm">
              {frenchSpaces('z = 0 : pas d’argument (M est en O).')}
            </p>
          ) : (
            <>
              <Formula
                tex={`\\cos\\theta = \\frac{a}{|z|} = ${cs.cos} \\qquad \\sin\\theta = \\frac{b}{|z|} = ${cs.sin}`}
              />
              <Formula tex={`\\theta = \\arg z ${g.exact ? '= ' : ''}${g.tex}`} />
              <p className="text-ink-600 text-sm">
                {frenchSpaces('L’argument principal, dans ]−π ; π] ; tout θ + 2kπ convient aussi.')}
              </p>
            </>
          )}
        </Box>
        {forms && (
          <Box title="Formes trigonométrique et exponentielle" testId="cp-polar">
            <Formula tex={`z = ${forms.trig}`} />
            <Formula tex={`z = ${forms.expo}`} />
          </Box>
        )}
        {conj && (
          <Box title="Conjugué" testId="cp-conj-box">
            <Formula tex={`\\overline{z} = ${zTex(zc)}`} />
            <Formula
              tex={`z\\,\\overline{z} = a^2 + b^2 = |z|^2 = ${numTex(z.a ** 2 + z.b ** 2)}`}
            />
            <p className="text-ink-600 text-sm">
              {frenchSpaces(
                'Symétrique de M par rapport à l’axe des réels : même module, argument opposé.',
              )}
            </p>
          </Box>
        )}
        {opp && (
          <Box title="Opposé" testId="cp-opp-box">
            <Formula tex={`-z = ${zTex(zo)}`} />
            <p className="text-ink-600 text-sm">
              {frenchSpaces(
                'Symétrique de M par rapport à O : même module, argument augmenté de π.',
              )}
            </p>
          </Box>
        )}
        {rot && (
          <Box title="Fois i" testId="cp-iz-box">
            <Formula tex={`iz = i\\left(${zTex(z)}\\right) = ${zTex(zi)}`} />
            <Formula tex={`i = e^{i\\frac{\\pi}{2}}`} />
            <p className="text-ink-600 text-sm">
              {frenchSpaces('Multiplier par i, c’est tourner d’un quart de tour autour de O.')}
            </p>
          </Box>
        )}
      </div>
    </div>
  );
}
